// hide-state.ts — "the user closed the widget", remembered for the tab, 12 hours at most.
//
// SESSION-scoped by decision (2026-08-27), not permanent and not per-page-view:
//
//  - A page-view scope would put the widget back ten seconds later on a sportsbook
//    the user is actively browsing, which reads as a broken close button.
//  - A permanent scope would burn inventory on a live pilot with no way back for
//    the user short of clearing site data — there is no undo on any of the three
//    hide paths, so the blast radius of one mis-tap has to stay bounded.
//
// `sessionStorage` matches that scope: it dies with the tab, survives reloads and
// SPA route changes in between, and is not shared with other tabs.
//
// ⭐ OWNER 2026-09-15 — a second chance:
//  - `v2` key. Everyone who closed the old templated-advice build (flag
//    `bc_hidden_v1:`) sees the new one again once, even in a tab open for days.
//    Measured 30d before the change: 12,129 of 28,821 launcher visitors closed it.
//  - A close lasts 12 hours at most. Phones keep a tab alive for days, and the
//    tab-life scope alone turned one close into a near-permanent hide there.

const KEY_PREFIX = "bc_hidden_v2:";

/** How long a close holds, at most. */
export const HIDE_TTL_MS = 12 * 60 * 60_000;

/** Namespaced per operator, like `bc_widget_pos_v2:` and `bc_fav_v1:` — one browser
 * can carry more than one operator (our own demo pages do). */
export function hideKey(apiKey: string): string {
  return KEY_PREFIX + apiKey;
}

/** Has the user closed the widget in this tab within the last 12 hours?
 *
 * Every access is guarded: Safari's private mode throws on `sessionStorage` rather
 * than returning null, and a throw here would take the whole widget down at mount.
 * Unreadable storage means "not hidden" — failing open shows the widget, which is
 * the recoverable direction. So does an unparseable or expired value. */
export function isHidden(apiKey: string, now: number = Date.now()): boolean {
  try {
    const raw = window.sessionStorage.getItem(hideKey(apiKey));
    if (raw == null) return false;
    const at = Number(raw);
    if (Number.isFinite(at) && at > 0 && at <= now && now - at < HIDE_TTL_MS) return true;
    window.sessionStorage.removeItem(hideKey(apiKey));   // expired or junk: forget it
    return false;
  } catch {
    return false;
  }
}

/** Remember the close, stamped with WHEN, so it can expire. */
export function markHidden(apiKey: string, now: number = Date.now()): void {
  try {
    window.sessionStorage.setItem(hideKey(apiKey), String(now));
  } catch {
    /* storage unavailable — the close still applies to this page view, it just
       won't survive a reload. Better than throwing out of the click handler. */
  }
}

/** Undo the session hide. Not reachable from the UI by design (there is no undo
 * affordance); this exists for `?bcdiag=1` and for tests. */
export function clearHidden(apiKey: string): void {
  try {
    window.sessionStorage.removeItem(hideKey(apiKey));
  } catch {
    /* nothing to do */
  }
}
